import Datatable from "@/components/tables/datatable";
import {
  useGetDashboardCsv,
  useGetDashboardData,
  useGetRevenueByCategory,
  useGetRevenueByProvider,
} from "@/services/admin.service";
import {
  convertUTCToLocal,
  convertUTCToLocalDay,
  getLocalTimestamp,
} from "@/utils/dex";
import { getKeyValue } from "@nextui-org/react";
import { Key, useCallback, useState } from "react";
import CustomDropdown from "../customdropdown";
import { ArrayItems } from "../customdropdown/dropdownarray";
import Card from "./Cards";
import PieChart from "./PieChart";
import Detailed from "./Detailed";
import Loading from "@/components/general/Loading";
import { formatLabel } from "@/utils/common";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

export interface GameCategoriesRevenue {
  category: string;
  revenue: number;
}

export interface ProvidersRevenue {
  provider: string;
  revenue: number;
}

const columns = [
  { key: "date", label: "Date", sortable: true },
  { key: "revenue", label: "Revenue(USD)", sortable: true },
];

const fields = [
  { label: "Date", value: "date" },
  { label: "Revenue", value: "revenue" },
];

const timePeriod: ArrayItems[] = [
  { label: "Daily", value: "daily" },
  { label: "Weekly", value: "weekly" },
  { label: "Monthly", value: "monthly" },
  { label: "Yearly", value: "yearly" },
];

const Dashboard = ({
  setSelectedTab,
}: {
  setSelectedTab: (tab: string) => void;
}) => {
  const [selectedAccount, setSelectedAccount] = useState<string>(
    timePeriod[0].value,
  );
  const query = useGetRevenueByProvider;
  const query1 = useGetRevenueByCategory;

  const { data: Data, isLoading } = useGetDashboardData(selectedAccount);
  const { data: CSVData, isLoading: isCSVLoading } =
    useGetDashboardCsv(selectedAccount);

  const handleDropdownSelect = (selectedOption: ArrayItems | null) => {
    if (selectedOption) {
      setSelectedAccount(selectedOption.value);
    }
  };

  const renderCell = useCallback((data: any[], item: any, columnKey: Key) => {
    switch (columnKey) {
      case "revenue":
        return (
          <div className="font-openSans text-[12px] font-normal text-[#FFFFFF]">
            {typeof getKeyValue(item, columnKey) === "number"
              ? (getKeyValue(item, columnKey) as number).toFixed(2)
              : "-"}
          </div>
        );
      case "date":
        return (
          <div className="font-openSans text-[12px] font-normal text-[#FFFFFF]">
            {convertUTCToLocalDay(getKeyValue(item, columnKey)) ?? "-"}
          </div>
        );
      default:
        return (
          <div className="font-normal">
            {getKeyValue(item, columnKey as string) || "-"}
          </div>
        );
    }
  }, []);

  const categories =
    Data?.data?.gameCategoriesRevenue?.map((item: GameCategoriesRevenue) => ({
      label: formatLabel(item.category, "category"),
      value: item.category,
    })) || [];

  const providers =
    Data?.data?.providersRevenue?.map((item: ProvidersRevenue) => ({
      label: formatLabel(item.provider, "provider"),
      value: item.provider,
    })) || [];

  console.log("categories", categories);

  const [selectedCategory, setSelectedCategory] = useState<string | null>(
    "SLOTS",
  );

  const [selectedProvider, setSelectedProvider] = useState<string | null>(
    "CREEDROOMZ",
  );

  if (isLoading) {
    return <Loading />;
  }

  const exportToExcel = async () => {
    const data = CSVData.data as Record<string, number | string>;

    const timestamp = getLocalTimestamp();

    const sheetData = [
      [`Dashboard Data (Timeline: ${data.timeline})`],
      [""],
      ["Casino Performance"],
      [
        "New Registration",
        "Total Registered Players",
        "Total Active Players",
        "Total Deposits",
        "Total Withdrawals",
        "Last Updated",
      ],
      [
        data.newRegistration,
        data.totalRegisteredPlayers,
        data.totalActivePlayers,
        data.totalDeposits,
        data.totalWithdrawals,
        data.lastUpdated,
      ],
      [""],
      ["Revenue by Game Type"],
      ["Game Type", "Revenue"],
      ...Object.keys(data)
        .filter((key) => key.startsWith("revenue_category_"))
        .map((key) => [
          key.replace("revenue_category_", "").replace(/_/g, " "),
          data[key] as number,
        ]),
      [""],
      ["Revenue by Provider"],
      ["Provider Name", "Revenue"],
      ...Object.keys(data)
        .filter((key) => key.startsWith("revenue_provider_"))
        .map((key) => [
          key.replace("revenue_provider_", "").replace(/_/g, " "),
          data[key] as number,
        ]),
      [""],
      ["Funds provided by the platform"],
      ["Total Funds", "Rakeback", "Bonuses"],
      [
        data.total_funds as number,
        data.rakeback as number,
        data.bonuses as number,
      ],
      [""],
      ["Players KYC status"],
      ["Total Players", "Verified Players", "Unverified Players"],
      [
        data.kyc_total as number,
        data.kyc_verified as number,
        data.kyc_unverified as number,
      ],
    ];

    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.aoa_to_sheet(sheetData);

    ws["!cols"] = [
      { wch: 25 },
      { wch: 25 },
      { wch: 25 },
      { wch: 25 },
      { wch: 20 },
      { wch: 20 },
      { wch: 30 },
      { wch: 15 },
    ];

    XLSX.utils.book_append_sheet(wb, ws, "Dashboard Data");

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const fileData = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });

    saveAs(fileData, `Dashboard data - ${timestamp}.csv`);
  };

  return (
    <div className="flex h-full w-full flex-col justify-evenly gap-3">
      <div className="flex w-full flex-col gap-6 rounded-[12px] bg-[#0E204D] p-6 md:flex-row">
        <div className="flex flex-1 flex-col items-start justify-start gap-3">
          <div className="whitespace-normal break-words font-openSans font-normal text-[#A6A6A6]">
            To learn about each player's information and valuation, you can go
            to
            <span className="font-bold"> Player Module</span>
          </div>

          <button
            className="text-md flex flex-row items-center gap-2 rounded-sm bg-button-gradient px-4 py-2 text-white"
            onClick={() => setSelectedTab("player-module")}
          >
            <div>Go to Player Module</div>
          </button>
        </div>
        <div className="w-[1px] bg-[#0F79E5]" />
        <div className="flex flex-1 flex-col items-start justify-start gap-3">
          <p className="whitespace-normal break-words font-openSans font-normal text-[#A6A6A6]">
            You can export data about
            <span className="font-bold">
              {" "}
              Casino Performance, GGR vs NGR trends, Revenue by Providers and
              Categories
            </span>
          </p>
          <button
            className="text-md flex flex-row items-center gap-2 rounded-sm bg-button-gradient px-4 py-2 text-white"
            onClick={exportToExcel}
          >
            <div>Export CSV Data</div>
          </button>
        </div>
      </div>

      <div className="flex w-full flex-col items-center justify-end gap-4 rounded-[12px] border-[1px] border-[#446BCE] p-3 md:flex-row">
        <div className="font-openSans font-normal text-[#F0F0F0]">
          Timeline View
        </div>
        <div className="w-full sm:w-1/6">
          <CustomDropdown
            options={timePeriod}
            onSelect={handleDropdownSelect}
            placeholder=""
            value={selectedAccount}
            dropdownColor="bg-[#0757DA] border-[#0757DA] hover:bg-[#0757DA]"
          />{" "}
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <Card
          title="New Registration"
          value={Data?.data.newRegistration}
          updatedTime={convertUTCToLocal(
            convertUTCToLocal(Data?.data.lastUpdated),
          )}
        />
        <Card
          title="Total registered Players"
          value={Data?.data.totalRegisteredPlayers}
          updatedTime={convertUTCToLocal(Data?.data.lastUpdated)}
        />
        <Card
          title="Total Active Players"
          value={Data?.data.totalActivePlayers}
          updatedTime={convertUTCToLocal(Data?.data.lastUpdated)}
        />
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <Card
          title="Total deposits"
          value={Data?.data.totalDeposits}
          updatedTime={convertUTCToLocal(Data?.data.lastUpdated)}
          currency={true}
        />
        <Card
          title="Total withdrawals"
          value={Data?.data.totalWithdrawals}
          updatedTime={convertUTCToLocal(Data?.data.lastUpdated)}
          currency={true}
        />
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div
          className="flex flex-col items-start justify-center gap-4 rounded-[12px] bg-[#001854] p-3"
          style={{ boxShadow: "0px 4px 20px 0px rgba(0, 0, 0, 0.1)" }}
        >
          <h1 className="font-openSans text-[18px] font-bold text-[#FFFFFF]">
            Game Categories Revenue
          </h1>
          <div className="flex w-full flex-grow flex-row items-start">
            <Detailed
              series={Data?.data.gameCategoriesRevenue
                .filter((item: GameCategoriesRevenue) => item.category !== "")
                .map((item: GameCategoriesRevenue) => item.revenue)}
              labels={Data?.data.gameCategoriesRevenue
                .filter((item: GameCategoriesRevenue) => item.category !== "")
                .map((item: GameCategoriesRevenue) => item.category)}
              type="category"
            />
          </div>
          <h1 className="mt-auto w-full text-center font-openSans text-[12px] font-normal italic text-[#A6A6A6]">
            Last updated on {convertUTCToLocal(Data?.data.lastUpdated)}
          </h1>
        </div>

        <div
          className="flex flex-col items-start justify-center gap-4 rounded-[12px] bg-[#001854] p-3"
          style={{ boxShadow: "0px 4px 20px 0px rgba(0, 0, 0, 0.1)" }}
        >
          <h1 className="font-openSans text-[18px] font-bold text-[#FFFFFF]">
            Providers Revenue
          </h1>
          <div className="flex w-full flex-grow flex-row items-start">
            <Detailed
              // series={ProvidersRevenue.series}
              // labels={ProvidersRevenue.labels}
              series={Data?.data.providersRevenue.map(
                (item: ProvidersRevenue) => item.revenue,
              )}
              labels={Data?.data.providersRevenue.map(
                (item: ProvidersRevenue) => item.provider,
              )}
              type="provider"
            />
          </div>
          <h1 className="mt-4 w-full text-center font-openSans text-[12px] font-normal italic text-[#A6A6A6]">
            Last updated on {convertUTCToLocal(Data?.data.lastUpdated)}
          </h1>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div
          className="flex flex-col items-start justify-center gap-4 rounded-[12px] bg-[#001854] p-3"
          style={{ boxShadow: "0px 4px 20px 0px rgba(0, 0, 0, 0.1)" }}
        >
          <h1 className="font-openSans text-[18px] font-bold text-[#FFFFFF]">
            Total Funds by Platform
          </h1>
          <h1 className="my-4 font-openSans text-[18px] font-bold text-[#FFFFFF]">
            US$
            <span className="text-[32px]">
              {Data.data.totalFundsByPlatform.records.rakeback +
                Data.data.totalFundsByPlatform.records.bonuses}
            </span>
          </h1>
          <div className="flex w-full flex-row">
            {/* {Data?.data?.totalFundsByPlatform?.records ? (
              <PieChart
                series={Object.values(Data.data.totalFundsByPlatform.records)}
                labels={Object.keys(Data.data.totalFundsByPlatform.records).map(
                  (key) => key.charAt(0).toUpperCase() + key.slice(1),
                )}
                colors={["#B139C1", "#E9FF27"]}
              />
            ) : (
              <Loading />
            )} */}

            <Detailed
              series={Object.values(Data.data.totalFundsByPlatform.records)}
              labels={Object.keys(Data.data.totalFundsByPlatform.records).map(
                (key) => key.charAt(0).toUpperCase() + key.slice(1),
              )}
              type="provider"
            />
          </div>
          <h1 className="mt-4 w-full text-center font-openSans text-[12px] font-normal text-[#A6A6A6]">
            Last updated on {convertUTCToLocal(Data?.data.lastUpdated)}
          </h1>
        </div>
        <div
          className="flex flex-col items-start justify-center gap-4 rounded-[12px] bg-[#001854] p-3"
          style={{ boxShadow: "0px 4px 20px 0px rgba(0, 0, 0, 0.1)" }}
        >
          <h1 className="w-full font-openSans text-[18px] font-bold text-[#FFFFFF]">
            Players KYC Status
          </h1>
          <h1 className="my-4 w-full text-center font-openSans text-[18px] font-bold text-[#FFFFFF]">
            Total registered players
            <span className="text-[32px]">
              {" "}
              {Data.data.playersKYCStatus.records.verified +
                Data.data.playersKYCStatus.records.unverified}
            </span>
          </h1>
          <div className="flex w-full flex-row">
            {/* {Data?.data?.playersKYCStatus?.records ? (
              <PieChart
                series={Object.values(Data.data.playersKYCStatus.records)}
                labels={Object.keys(Data.data.playersKYCStatus.records).map(
                  (key) => key.charAt(0).toUpperCase() + key.slice(1),
                )}
                colors={["#39BAC1", "#E9FF27"]}
              />
            ) : (
              <Loading />
            )} */}

            <Detailed
              series={Object.values(Data.data.playersKYCStatus.records)}
              labels={Object.keys(Data.data.playersKYCStatus.records).map(
                (key) => key.charAt(0).toUpperCase() + key.slice(1),
              )}
              type="provider"
              dollar={false}
            />
          </div>
          <h1 className="mt-4 w-full text-center font-openSans text-[12px] font-normal text-[#A6A6A6]">
            Last updated on {convertUTCToLocal(Data?.data.lastUpdated)}
          </h1>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div
          className="flex flex-col items-start justify-start rounded-[12px] bg-[#001854] p-3 md:justify-center"
          style={{ boxShadow: "0px 4px 20px 0px rgba(0, 0, 0, 0.1)" }}
        >
          <div className="flex w-full flex-col items-start justify-between gap-y-2  md:flex-row md:items-center">
            <h1 className="font-openSans text-[18px] font-bold text-[#FFFFFF]">
              Revenue by Category
            </h1>
            <div className="min-w-[180px] sm:w-1/6">
              <CustomDropdown
                options={categories}
                onSelect={(e) => {
                  setSelectedCategory(e?.value || null);
                }}
                placeholder="Select Category"
                value={selectedCategory ?? ""}
                dropdownColor="bg-[#0757DA] border-[#0757DA] hover:bg-[#0757DA]"
              />
            </div>
          </div>

          <div className="w-full">
            <Datatable
              columns={columns}
              renderCell={renderCell}
              defaultRowsPerPage={"10"}
              query={query1}
              customQueryFilter={{
                category: selectedCategory,
              }}
              autoFetch
              searchFields={fields}
              hidePagination
              tableSize={232}
              hideFilter
              updatedTimeData
              customJson
              timeline={selectedAccount}
            />
          </div>
        </div>
        <div
          className="flex flex-col items-start justify-center rounded-[12px] bg-[#001854] p-3"
          style={{ boxShadow: "0px 4px 20px 0px rgba(0, 0, 0, 0.1)" }}
        >
          <div className="flex w-full flex-col items-start justify-between gap-y-2  md:flex-row md:items-center">
            <h1 className="font-openSans text-[18px] font-bold text-[#FFFFFF]">
              Revenue by provider
            </h1>
            <div className="min-w-[180px] sm:w-1/6">
              <CustomDropdown
                options={providers}
                onSelect={(e) => {
                  setSelectedProvider(e?.value || null);
                }}
                placeholder="Select Provider"
                value={selectedProvider ?? ""}
                dropdownColor="bg-[#0757DA] border-[#0757DA] hover:bg-[#0757DA]"
              />
            </div>
          </div>

          <div className="w-full">
            <Datatable
              columns={columns}
              renderCell={renderCell}
              defaultRowsPerPage={"10"}
              query={query}
              customQueryFilter={{
                provider: selectedProvider,
              }}
              autoFetch
              searchFields={fields}
              hidePagination
              tableSize={232}
              hideFilter
              updatedTimeData
              customJson
              timeline={selectedAccount}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
